import React, { useState } from 'react';
import { Palette, Loader2, Check, X } from 'lucide-react';
import { FormData, Errors } from './SettingList';
import { useSettings } from '../../../src/context/SettingsContext';

interface AppSectionProps {
    formData: FormData
    errors: Errors
    handleInputChange: (field: keyof FormData, value: string) => void
    handleReset: () => void
    handleSubmit: () => void
    isLoading: boolean
}

const AppSection: React.FC<AppSectionProps> = ({
    formData,
    errors,
    handleInputChange,
    handleReset,
    handleSubmit,
    isLoading,
}) => {
    const { fetchSettings } = useSettings()
    const token = sessionStorage.getItem("access_token")
    const [isResetModalOpen, setIsResetModalOpen] = useState<boolean>(false)

    React.useEffect(() => {
        if (token) {
            fetchSettings()
        }
    }, [fetchSettings, token])

    const confirmReset = () => {
        handleReset()
        setIsResetModalOpen(false)
    }

    // Function to determine if a color is light or dark
    const isLightColor = (hexColor: string): boolean => {
        // Remove # if present
        const hex = hexColor.replace("#", "")

        // Convert to RGB
        const r = Number.parseInt(hex.substr(0, 2), 16)
        const g = Number.parseInt(hex.substr(2, 2), 16)
        const b = Number.parseInt(hex.substr(4, 2), 16)

        // Calculate luminance using the formula for relative luminance
        const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255

        // Return true if the color is light (luminance > 0.5)
        return luminance > 0.5
    }

    // Function to get the appropriate icon color based on background
    const getIconColor = (backgroundColor: string): string => {
        if (!backgroundColor) return "white"
        return isLightColor(backgroundColor) ? "black" : "white"
    }

    const colorFields = [
        { name: "sidebarColor", label: "Sidebar Color" },
        { name: "headerColor", label: "Header Color" },
        { name: "menuActiveColor", label: "Menu Active Color" },
        { name: "fontColor", label: "Font Color" },
    ]

    return (
        <div className="space-y-4">
            {isResetModalOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-4 w-full max-w-sm transform transition-all duration-200 scale-100">
                        <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-3 text-center">Reset App Settings</h3>
                        <p className="text-xs text-gray-600 dark:text-gray-300 mb-4 text-center">
                            Are you sure you want to reset the App Section form to default values? This action cannot be undone.
                        </p>
                        <div className="flex justify-center gap-3">
                            <button
                                onClick={() => setIsResetModalOpen(false)}
                                className="px-3 py-1.5 bg-gray-200 dark:bg-gray-600 text-gray-800 dark:text-gray-200 rounded-md hover:bg-gray-300 dark:hover:bg-gray-500 transition-all duration-200 text-xs"
                            >
                                No, Cancel
                            </button>
                            <button
                                onClick={confirmReset}
                                className="px-3 py-1.5 bg-green-500 text-white rounded-md hover:bg-green-600 transition-all duration-200 text-xs"
                            >
                                Yes, Reset
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {colorFields.map(({ name, label }) => {
                    const currentColor =
                        formData[name as keyof FormData] ||
                        (name === "sidebarColor"
                            ? "#ffffff"
                            : name === "headerColor"
                                ? "#ffffff"
                                : name === "menuActiveColor"
                                    ? "#ede9e9"
                                    : "#000000")

                    const iconColor = getIconColor(currentColor)

                    return (
                        <div
                            key={name}
                            className="rounded-lg p-3 border bg-gradient-to-br from-gray-100 to-gray-50 dark:from-gray-800 dark:to-gray-900"
                        >
                            <h3 className="text-base font-semibold text-gray-800 dark:text-white mb-2 flex items-center">
                                <div
                                    className="w-6 h-6 rounded-md flex items-center justify-center mr-1 transition-all duration-200"
                                    style={{ backgroundColor: currentColor }}
                                >
                                    <Palette className="w-3 h-3 transition-colors duration-200" style={{ color: iconColor }} />
                                </div>
                                {label}
                            </h3>

                            <div className="space-y-1.5">
                                <div className="flex items-center justify-center gap-2">
                                    <input
                                        type="color"
                                        value={currentColor}
                                        onChange={(e) => handleInputChange(name as keyof FormData, e.target.value)}
                                        className="w-8 h-8 rounded-md border-2 border-white shadow-sm cursor-pointer hover:scale-105 transition-transform flex-shrink-0"
                                    />
                                    <div className="flex-1 relative">
                                        <input
                                            type="text"
                                            value={formData[name as keyof FormData] || ""}
                                            onChange={(e) => handleInputChange(name as keyof FormData, e.target.value)}
                                            className={`w-full px-2 py-1.5 h-8 bg-white dark:bg-gray-800 border rounded-md text-gray-700 dark:text-gray-200 text-xs font-mono focus:ring-1 focus:outline-none transition-all duration-200 ${errors[name]
                                                ? "border-red-500 focus:ring-red-500"
                                                : "border-gray-300 dark:border-gray-600 focus:ring-purple-500"
                                                }`}
                                            placeholder={`Enter ${label.toLowerCase()}`}
                                        />
                                        {formData[name as keyof FormData] && !errors[name] ? (
                                            <Check className="absolute right-2 top-1/2 transform -translate-y-1/2 w-3 h-3 text-green-500" />
                                        ) : errors[name] ? (
                                            <X className="absolute right-2 top-1/2 transform -translate-y-1/2 w-3 h-3 text-red-500" />
                                        ) : null}
                                    </div>
                                </div>

                                {errors[name] && (
                                    <div className="pl-10">
                                        <p className="text-red-500 text-xs text-left">{errors[name]}</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    )
                })}
            </div>

            <div className="flex justify-end gap-1.5 pt-2">
                <button
                    type="button"
                    onClick={() => setIsResetModalOpen(true)}
                    className="bg-white hover:bg-gray-100 text-black px-3 py-1.5 rounded-md font-medium transition-all shadow-sm text-xs border border-gray-300"
                    disabled={isLoading}
                >
                    Reset
                </button>
                <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={isLoading}
                    className="bg-[#2F6AA7] hover:bg-[#265891] text-white px-4 py-1.5 rounded-md font-medium transition-all shadow-sm text-xs flex items-center disabled:opacity-50"
                >
                    {isLoading ? <Loader2 className="w-3 h-3 mr-0.5 animate-spin" /> : null}
                    Save App Settings
                </button>
            </div>
        </div>
    )
}

export default AppSection
  